import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config.js';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

const DynamicSubstituteRegistration = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      // Extract tenant subdomain from hostname
      const subdomain = window.location.hostname.split('.')[0];
      console.log('Extracted subdomain:', subdomain);

      if (!subdomain || subdomain === 'localhost') {
        setMessage('Invalid registration link');
        setLoading(false);
        return;
      }

      // Find tenant by subdomain
      const tenantsQuery = query(
        collection(db, 'tenants'), 
        where('subdomain', '==', subdomain)
      );
      const tenantsSnapshot = await getDocs(tenantsQuery);
      
      if (tenantsSnapshot.empty) {
        console.error('No tenant found for subdomain:', subdomain);
        setMessage('Invalid registration link');
        setLoading(false);
        return;
      }

      const tenantDoc = tenantsSnapshot.docs[0];
      const tenantData = tenantDoc.data();
      const tenantId = tenantDoc.id;
      
      console.log('Found tenant:', tenantData);
      setTenantInfo(tenantData);

      // Look for substitute registration configuration
      const configQuery = query(
        collection(db, 'tenantConfigurations'),
        where('tenantId', '==', tenantId),
        where('configKey', '==', 'substitute_registration')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        console.error('No substitute registration config found for tenant:', tenantId);
        setMessage('Registration form not configured');
        setLoading(false);
        return;
      }

      const configDoc = configSnapshot.docs[0];
      const formConfig = configDoc.data().configValue;
      
      console.log('SUBSTITUTE CONFIG LOADED:', formConfig);
      setConfig({ ...formConfig, tenantId }); // Include tenantId in config
      
      // Check if access code is required
      if (!formConfig.settings?.requireAccessCode) {
        setAccessGranted(true);
      }

    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage('Error loading registration form');
    }
    setLoading(false);
  };

  const handleAccessCodeSubmit = () => {
    if (accessCode === config.settings.accessCode) {
      setAccessGranted(true);
      setMessage('');
    } else {
      setMessage('Invalid access code');
    }
  };

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleMultiSelectChange = (fieldKey, value, checked) => {
    setFormData(prev => {
      const currentValues = prev[fieldKey] || [];
      if (checked) {
        return {
          ...prev,
          [fieldKey]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [fieldKey]: currentValues.filter(v => v !== value)
        };
      }
    });
  };

  const validateForm = () => {
    const errors = [];
    
    // Check required fields in each enabled section
    Object.entries(config.sections || {}).forEach(([sectionKey, section]) => {
      if (section.enabled) {
        Object.entries(section.fields || {}).forEach(([fieldKey, field]) => {
          if (field.enabled && field.required) {
            const value = formData[fieldKey];
            if (!value || (Array.isArray(value) && value.length === 0)) {
              errors.push(`${field.label} is required`);
            }
          }
        });
      }
    });

    // Check custom fields
    config.customFields?.forEach(field => {
      if (field.enabled && field.required) {
        const value = formData[field.id];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors.push(`${field.label} is required`);
        }
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    const errors = validateForm();
    if (errors.length > 0) {
      setMessage(errors.join(', '));
      setSubmitting(false);
      return;
    }

    try {
      const registrationData = {
        ...formData,
        tenantId: config.tenantId,
        status: config.settings?.requireApproval ? 'pending' : 'approved',
        submittedAt: serverTimestamp(),
        registrationType: 'substitute'
      };

      console.log('Submitting registration data:', registrationData);
      await addDoc(collection(db, 'substituteRegistrations'), registrationData);
      
      setMessage('Registration submitted successfully! You will be notified when reviewed.');
      setFormData({});
    } catch (error) {
      console.error('Error submitting registration:', error);
      setMessage('Error submitting registration. Please try again.');
    }
    
    setSubmitting(false);
  };

  const renderField = (sectionKey, fieldKey, field) => {
    const value = formData[fieldKey] || '';
    const fieldId = `${sectionKey}_${fieldKey}`;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
      case 'date':
        return (
          <input
            type={field.type}
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            required={field.required}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
        );

      case 'select':
        return (
          <select
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            required={field.required}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          >
            <option value="">-- Select --</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '12px', maxHeight: '150px', overflowY: 'auto' }}>
            {field.options?.map(option => (
              <label key={option} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={(formData[fieldKey] || []).includes(option)}
                  onChange={(e) => handleMultiSelectChange(fieldKey, option, e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                {option}
              </label>
            ))}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            required={field.required}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        );

      default:
        return null;
    }
  };

  const renderSection = (sectionKey, sectionTitle, sectionDescription) => {
    const section = config.sections?.[sectionKey];
    if (!section?.enabled) return null;

    const availableFields = Object.keys(section.fields || {});
    
    const fieldPriorities = {
      firstName: 1, lastName: 2, email: 3, phone: 4, address: 5, city: 6, state: 7, zip: 8,
      emergencyName: 1, emergencyPhone: 2, emergencyRelation: 3,
      yearsExperience: 1, teachingLicense: 2, educationLevel: 3, preferredSubjects: 4,
      availableStartDate: 1, gradePreferences: 2, daysAvailable: 3
    };

    const sortedFields = availableFields.sort((a, b) => {
      const priorityA = fieldPriorities[a] || 999;
      const priorityB = fieldPriorities[b] || 999;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      return a.localeCompare(b);
    });

    return (
      <div key={sectionKey} style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#1976d2', fontSize: '20px', marginBottom: '10px' }}>
          {sectionTitle}
        </h2>
        {sectionDescription && (
          <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
            {sectionDescription}
          </p>
        )}
        
        <div style={{ display: 'grid', gap: '20px' }}>
          {sortedFields.map((fieldKey) => {
            const field = section.fields[fieldKey];
            if (!field?.enabled) return null;

            return (
              <div key={fieldKey}>
                <label 
                  htmlFor={`${sectionKey}_${fieldKey}`}
                  style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {field.label} {field.required && <span style={{ color: '#dc3545' }}>*</span>}
                </label>
                {renderField(sectionKey, fieldKey, field)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h1>Substitute Registration</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!config || message === 'Invalid registration link' || message === 'Registration form not configured') {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h1>Registration Unavailable</h1>
          <p>{message || 'This registration form is not available.'}</p>
        </div>
      </div>
    );
  }

  // Access code screen
  if (config.settings?.requireAccessCode && !accessGranted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '40px 20px' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>Substitute Registration</h1>
            <p style={{ margin: 0, color: '#666' }}>
              {tenantInfo?.organizationName || tenantInfo?.name || 'Organization'}
            </p>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔐</div>
            <h3 style={{ color: '#333', marginBottom: '15px' }}>Access Code Required</h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Please enter the access code to continue with your substitute registration.
            </p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Access Code
            </label>
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter access code"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                textAlign: 'center',
                letterSpacing: '1px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {message && (
            <div style={{ 
              color: '#dc3545', 
              textAlign: 'center', 
              marginBottom: '20px',
              backgroundColor: '#f8d7da',
              padding: '12px',
              borderRadius: '4px'
            }}>
              {message}
            </div>
          )}

          <button
            onClick={handleAccessCodeSubmit}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Continue to Registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '10px' }}>Substitute Registration</h1>
        
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          {tenantInfo?.organizationName || tenantInfo?.name || 'Organization'}
        </p>

        {config.settings?.welcomeMessage && (
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
            {config.settings.welcomeMessage}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          {renderSection('personalInfo', 'Personal Information', 'Basic contact and personal details')}
          {renderSection('qualifications', 'Qualifications', 'Your education and teaching credentials')}
          {renderSection('experience', 'Experience', 'Your teaching and work experience')}
          {renderSection('preferences', 'Teaching Preferences', 'Subject areas and grade levels you prefer')}
          {renderSection('availability', 'Availability', 'When are you available to substitute teach?')}
          {renderSection('emergency', 'Emergency Contact', 'Emergency contact information')}
          {renderSection('additional', 'Additional Information', 'Any other information you would like to share')}

          {/* Render custom fields */}
          {config.customFields && config.customFields.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ color: '#1976d2', fontSize: '20px', marginBottom: '20px' }}>
                Additional Information
              </h2>
              
              <div style={{ display: 'grid', gap: '20px' }}>
                {config.customFields.map((field) => {
                  if (!field.enabled) return null;

                  return (
                    <div key={field.id}>
                      <label 
                        htmlFor={field.id}
                        style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontWeight: '500',
                          fontSize: '14px'
                        }}
                      >
                        {field.label} {field.required && <span style={{ color: '#dc3545' }}>*</span>}
                      </label>
                      {renderField('custom', field.id, field)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          {config.settings?.termsAndConditions && (
            <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '14px', color: '#666' }}>
              {config.settings.termsAndConditions}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '18px',
              fontWeight: '500',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Registration'}
          </button>

          {message && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: message.includes('Error') || message.includes('Invalid') ? '#f8d7da' : '#d4edda',
              color: message.includes('Error') || message.includes('Invalid') ? '#721c24' : '#155724',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default DynamicSubstituteRegistration;